import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import { WebsiteProvider } from "@/lib/website";
import { ContentPage, NotFound } from "@/components/PublishingContent";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";

const WebsitePreview = lazy(() => import("@/pages/WebsitePreview"));
const Packages = lazy(() => import("@/pages/Packages"));
const HowItWorks = lazy(() => import("@/pages/HowItWorks"));
const AiHumanReview = lazy(() => import("@/pages/AiHumanReview"));
const KdpPublishing = lazy(() => import("@/pages/KdpPublishing"));
const Faq = lazy(() => import("@/pages/Faq"));
const Contact = lazy(() => import("@/pages/Contact"));
const OrderConfirmation = lazy(() => import("@/pages/OrderConfirmation"));
const OrderStatus = lazy(() => import("@/pages/OrderStatus"));
const ProjectPayment = lazy(() => import("@/pages/ProjectPayment"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const Refund = lazy(() => import("@/pages/Refund"));
const Admin = lazy(() => import("@/pages/Admin"));

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
    else requestAnimationFrame(() => document.getElementById(hash.slice(1))?.scrollIntoView());
  }, [pathname, hash]);
  return null;
}

function LegacyEnquiryRedirect() {
  const { search } = useLocation();
  return <Navigate replace to={`/discuss-my-book${search}#enquiry`} />;
}
function CustomPage() { const { slug } = useParams(); return <ContentPage slug={slug} />; }

function App() {
  return (
    <div className="App bg-[#F6F3EC] text-[#3A3A3A] min-h-screen">
      <BrowserRouter>
        <Suspense fallback={<div className="p-16 text-center text-[#3A3A3A]">Loading…</div>}>
          <Routes>
            <Route path="/admin" element={<><ScrollToTop /><Admin /></>} />
            <Route path="/admin/website-preview" element={<WebsitePreview />} />
            <Route element={<WebsiteProvider><Layout /></WebsiteProvider>}>
              <Route path="/" element={<><ScrollToTop /><ContentPage /></>} />
              <Route path="/discuss-my-book" element={<><ScrollToTop /><ContentPage slug="discuss-my-book" /></>} />
              <Route path="/pages/:slug" element={<><ScrollToTop /><CustomPage /></>} />
              <Route path="/packages" element={<><ScrollToTop /><Packages /></>} />
              <Route path="/how-it-works" element={<><ScrollToTop /><HowItWorks /></>} />
              <Route path="/ai-and-human-review" element={<><ScrollToTop /><AiHumanReview /></>} />
              <Route path="/kdp-publishing" element={<><ScrollToTop /><KdpPublishing /></>} />
              <Route path="/faq" element={<><ScrollToTop /><Faq /></>} />
              <Route path="/contact" element={<><ScrollToTop /><Contact /></>} />
              <Route path="/start-your-book" element={<LegacyEnquiryRedirect />} />
              <Route path="/order-confirmation" element={<><ScrollToTop /><OrderConfirmation /></>} />
              <Route path="/order-status" element={<><ScrollToTop /><OrderStatus /></>} />
              <Route path="/project" element={<><ScrollToTop /><ProjectPayment /></>} />
              <Route path="/privacy-policy" element={<><ScrollToTop /><Privacy /></>} />
              <Route path="/terms-and-conditions" element={<><ScrollToTop /><Terms /></>} />
              <Route path="/refund-policy" element={<><ScrollToTop /><Refund /></>} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </Suspense>
        <Toaster richColors position="top-center" />
      </BrowserRouter>
    </div>
  );
}

export default App;
